import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileClock, FileUp, FolderOpenDot, RefreshCcw, Trash2 } from 'lucide-react';
import LaborStandaloneLayout from '../../components/labor/LaborStandaloneLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import {
  clearLaborIntakeDraft,
  getLaborIntakeDraft,
} from '../../features/labor-intake/laborIntakeService';

const formatDate = (value) =>
  new Intl.DateTimeFormat('ar-SA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));

const LaborIntakeDraftPage = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const draft = useMemo(() => getLaborIntakeDraft(), []);

  const handleDiscardDraft = () => {
    clearLaborIntakeDraft();
    navigate('/labor-intake/new');
  };

  return (
      <LaborStandaloneLayout
      badgeText={isArabic ? 'استكمال الطلب' : 'Resume Request'}
      footerNote={isArabic ? 'يمكنكم من هذه الصفحة استكمال المسودة المحفوظة أو الانتقال إلى صفحة المتابعة.' : 'From this page, you can resume the saved draft or go to the tracking page.'}
    >
      <main className="px-4 pb-20 pt-12">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-8 lg:grid-cols-[1.15fr_.85fr]">
            <Card className="rounded-[30px] border-[#C8A870]/20 bg-white/95 shadow-[0_18px_55px_rgba(15,23,42,0.08)]">
              <CardHeader>
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-[24px] bg-[#1F3B64] text-white">
                  <FileClock className="h-7 w-7" />
                </div>
                <CardTitle className="text-3xl font-black text-[#1F3B64]">{isArabic ? 'استكمال طلب محفوظ' : 'Resume a Saved Request'}</CardTitle>
                <CardDescription className="text-sm leading-7 text-slate-600">
                  {isArabic ? 'إذا سبق حفظ الطلب أثناء التعبئة، يمكن استكماله من النقطة التي توقفتم عندها، أو البدء بطلب جديد عند الحاجة.' : 'If the request was previously saved during completion, you can resume it from where you stopped or start a new request if needed.'}
                </CardDescription>
              </CardHeader>

              <CardContent>
                {draft ? (
                  <div className="space-y-5">
                    <div className="rounded-[24px] border border-[#C8A870]/18 bg-[#f8f6f1] p-5">
                      <p className="text-sm font-bold text-[#1F3B64]">{isArabic ? 'تم العثور على مسودة محفوظة' : 'A saved draft was found'}</p>
                      <div className="mt-4 grid gap-3 text-sm text-slate-700 sm:grid-cols-2">
                        <InfoRow
                          label={isArabic ? 'رقم المسودة' : 'Draft reference'}
                          value={draft.draftReference || (isArabic ? 'غير متوفر' : 'Not available')}
                        />
                        <InfoRow
                          label={isArabic ? 'آخر حفظ' : 'Last saved'}
                          value={draft._savedAt ? formatDate(draft._savedAt) : isArabic ? 'غير متوفر' : 'Not available'}
                        />
                        <InfoRow
                          label={isArabic ? 'اسم العميل' : 'Client name'}
                          value={draft.client?.fullName || (isArabic ? 'لم يُسجل بعد' : 'Not entered yet')}
                        />
                        <InfoRow
                          label={isArabic ? 'Employer' : 'Employer'}
                          value={draft.company?.employerName || (isArabic ? 'لم تُسجل بعد' : 'Not entered yet')}
                        />
                        <InfoRow
                          label={isArabic ? 'الجوال' : 'Mobile'}
                          value={draft.client?.mobile || (isArabic ? 'لم يُسجل بعد' : 'Not entered yet')}
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <Button
                        type="button"
                        onClick={() => navigate('/labor-intake/new')}
                        className="rounded-xl bg-[#1F3B64] px-6 py-6 text-white hover:bg-[#183553]"
                      >
                        {isArabic ? 'استكمال المسودة' : 'Resume Draft'}
                        <RefreshCcw className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleDiscardDraft}
                        className="rounded-xl border-[#C8A870]/35 px-6 py-6 text-[#1F3B64]"
                      >
                        {isArabic ? 'بدء طلب جديد' : 'Start New Request'}
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-[24px] border border-[#C8A870]/18 bg-[#f8f6f1] p-6">
                    <p className="text-lg font-black text-[#1F3B64]">{isArabic ? 'لا توجد مسودة محفوظة حالياً' : 'No saved draft currently exists'}</p>
                    <p className="mt-3 text-sm leading-7 text-slate-600">
                      {isArabic ? 'يمكنكم البدء بطلب جديد، وسيتم حفظ التقدم تلقائياً أثناء التعبئة حتى يتاح استكماله لاحقاً من هذه الصفحة.' : 'You can start a new request, and your progress will be saved automatically so it can be resumed later from this page.'}
                    </p>
                    <Button
                      type="button"
                      onClick={() => navigate('/labor-intake/new')}
                      className="mt-5 rounded-xl bg-[#1F3B64] px-6 py-6 text-white hover:bg-[#183553]"
                    >
                      {isArabic ? 'بدء طلب جديد' : 'Start New Request'}
                      <FolderOpenDot className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="rounded-[30px] border-[#C8A870]/20 bg-white/95 shadow-[0_18px_55px_rgba(15,23,42,0.08)]">
              <CardHeader>
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-[24px] bg-[#A27849] text-white">
                  <FileUp className="h-7 w-7" />
                </div>
                <CardTitle className="text-2xl font-black text-[#1F3B64]">{isArabic ? 'رفع نواقص أو مستندات إضافية' : 'Upload Missing or Additional Documents'}</CardTitle>
                <CardDescription className="text-sm leading-7 text-slate-600">
                  {isArabic ? 'إذا كان الطلب مُرسلاً مسبقاً وجرى طلب مستندات إضافية، يمكن الانتقال إلى صفحة المتابعة واستخدام الرقم المرجعي ورمز المتابعة لرفع الملفات المطلوبة.' : 'If the request has already been submitted and additional documents were requested, you can go to the tracking page and use the reference number and tracking token to upload the required files.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-[24px] border border-[#C8A870]/18 bg-[#f8f6f1] p-5 text-sm leading-7 text-slate-600">
                  {isArabic ? 'صفحة المتابعة مخصصة لعرض الحالة الحالية فقط، مع إمكانية رفع المستندات الإضافية دون إظهار أي ملاحظات داخلية.' : 'The tracking page only shows the current request status and allows additional uploads without displaying any internal notes.'}
                </div>

                <Button asChild className="w-full rounded-xl bg-[#1F3B64] py-6 text-white hover:bg-[#183553]">
                  <Link to="/labor-intake/track">
                    {isArabic ? 'الذهاب إلى صفحة المتابعة' : 'Go to Tracking Page'}
                    <FileUp className="h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </LaborStandaloneLayout>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="rounded-2xl border border-white bg-white px-4 py-3">
    <p className="text-xs font-bold text-[#A27849]">{label}</p>
    <p className="mt-2 text-sm font-semibold text-[#1F3B64]">{value}</p>
  </div>
);

export default LaborIntakeDraftPage;
